import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { AuthGuard } from '@/components/AuthGuard';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { AlunosPage } from '@/pages/alunos/AlunosPage';
import { NovoAlunoPage } from '@/pages/alunos/NovoAlunoPage';
import { AlunoDetailPage } from '@/pages/alunos/AlunoDetailPage';
import { EmpresasPage } from '@/pages/empresas/EmpresasPage';
import { NovaEmpresaPage } from '@/pages/empresas/NovaEmpresaPage';
import { CidadesPage } from '@/pages/cidades/CidadesPage';
import { NovaCidadePage } from '@/pages/cidades/NovaCidadePage';
import { CursosPage } from '@/pages/cursos/CursosPage';
import { NovoCursoPage } from '@/pages/cursos/NovoCursoPage';
import { EstagiosPage } from '@/pages/estagios/EstagiosPage';
import { NovaEstagioPage } from '@/pages/estagios/NovaEstagioPage';
import { VerEstagioPage } from '@/pages/estagios/VerEstagioPage';
import { InstituicoesPage } from '@/pages/instituicoes/InstituicoesPage';
import { NovaInstituicaoPage } from '@/pages/instituicoes/NovaInstituicaoPage';
import { SeguradorasPage } from '@/pages/seguradoras/SeguradorasPage';
import { NovaSeguradoraPage } from '@/pages/seguradoras/NovaSeguradoraPage';
import { HorariosPage } from '@/pages/horarios/HorariosPage';
import { NovoHorarioPage } from '@/pages/horarios/NovoHorarioPage';
import { VerHorariosEstagioPage } from '@/pages/horarios/VerHorariosEstagioPage';
import { RelatoriosPage } from '@/pages/relatorios/RelatoriosPage';
import { NovoRelatorioPage } from '@/pages/relatorios/NovoRelatorioPage';
import { TermosPage } from '@/pages/termos/TermosPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

// Protected Routes Component
const ProtectedRoutes: React.FC = () => {
  return (
    <AuthGuard>
      <Layout>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/alunos" element={<AlunosPage />} />
          <Route path="/alunos/:id" element={<AlunoDetailPage />} />
          <Route path="/alunos/novo" element={<NovoAlunoPage />} />
          <Route path="/alunos/:id/editar" element={<NovoAlunoPage />} />
          <Route path="/empresas" element={<EmpresasPage />} />
          <Route path="/empresas/nova" element={<NovaEmpresaPage />} />
          <Route path="/empresas/:id/editar" element={<NovaEmpresaPage />} />
          <Route path="/cidades" element={<CidadesPage />} />
          <Route path="/cidades/nova" element={<NovaCidadePage />} />
          <Route path="/cidades/:id/editar" element={<NovaCidadePage />} />
          <Route path="/cursos" element={<CursosPage />} />
          <Route path="/cursos/novo" element={<NovoCursoPage />} />
          <Route path="/cursos/:id/editar" element={<NovoCursoPage />} />
          <Route path="/estagios" element={<EstagiosPage />} />
          <Route path="/estagios/novo" element={<NovaEstagioPage />} />
          <Route path="/estagios/:id" element={<VerEstagioPage />} />
          <Route path="/estagios/:id/editar" element={<NovaEstagioPage />} />
          <Route path="/instituicoes" element={<InstituicoesPage />} />
          <Route path="/instituicoes/nova" element={<NovaInstituicaoPage />} />
          <Route path="/instituicoes/:id/editar" element={<NovaInstituicaoPage />} />
          <Route path="/seguradoras" element={<SeguradorasPage />} />
          <Route path="/seguradoras/nova" element={<NovaSeguradoraPage />} />
          <Route path="/seguradoras/:id/editar" element={<NovaSeguradoraPage />} />
          <Route path="/horarios" element={<HorariosPage />} />
          <Route path="/horarios/novo" element={<NovoHorarioPage />} />
          <Route path="/horarios/:id/editar" element={<NovoHorarioPage />} />
          <Route path="/horarios/estagio/:estagioId" element={<VerHorariosEstagioPage />} />
          <Route path="/relatorios" element={<RelatoriosPage />} />
          <Route path="/relatorios/novo" element={<NovoRelatorioPage />} />
          <Route path="/relatorios/:id/editar" element={<NovoRelatorioPage />} />
          <Route path="/gerar-termo" element={<TermosPage />} />
          {/* Add more protected routes here */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </AuthGuard>
  );
};

// Public Routes Component
const PublicRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

// Main App Component
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <ProtectedRoutes /> : <PublicRoutes />;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <div className="App">
              <AppContent />
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: '#10b981',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />
            </div>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
